import React, { useContext, useState, useCallback, useRef, useMemo, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Tooltip, Checkbox } from 'antd';
import { useTranslation } from 'react-i18next';
import _ from 'underscore';
import { PageElementsContext } from '../../../context/pageElementsContext';
import { CheckBoxSection } from '../../../artboard/Page/styles';
import ColorPicker from '../../../artboard/ColorPicker';
import { ColorButton } from '../../../artboard/Toolbar/styles';
import FillableCheckBoxSettings from '../../../components/GroupSettings/IndividualBoxSettings';
import {
  OPTIONS_KEY,
  FIELD_NAME_KEY,
  CHECK_TYPE_KEY,
  IS_VARIABLE_KEY,
  TEXT_DEBOUNCE_TIME_OUT,
  READ_ONLY_KEY,
  GROUP_NAME_KEY,
  FILLABLE_CHECKBOX_TYPE,
  dummyCallback,
} from '../../../utils/complexDocumentConstants';
import { ELEMENTS_SETTINGS_TOOLBAR } from '../../../artboard/Toolbar/translationValues';
import { deepCopy, random4Characters } from '../../../utils/javascript';
import { v4 } from 'uuid';
import { checkBoxTextElement } from '../../../utils/masterElements/fillableInputs';
import { useDocumentContext } from '../../../context/complexDocumentContext';
import { CONTEXT_KEYS, useArtboardContext } from '../../../context/complexArtboardContext';
import { FillableElementWrapper } from '../styles';

const settingsGetter = el => el[OPTIONS_KEY];
const getElementDetails = el => el;

const getCheckBoxClassName = checkType => {
  let additionClassName = '';
  switch (checkType) {
    case '8':
      additionClassName = 'crossBox';
      break;
    case '4':
      additionClassName = 'checkBox';
      break;
    case 'n':
      additionClassName = 'squareBox';
      break;
    case 'l':
      additionClassName = 'circleBox';
      break;
    default:
      additionClassName = 'checkBox';
  }
  return additionClassName;
};

const allStyleKeys = ['borderColor', "backgroundColor", "color"];
const [borderColorKey, ...styleKeys] = allStyleKeys;
const NAMESPACES = ['ELEMENTS_SETTINGS_TOOLBAR', 'FILLABLE_ELEMENTS_MAIN_CONTENT'];

const ReusableCheckbox = props => {
  const {
    element,
    editable,
    selected,
    refreshSelectedElementIds = dummyCallback,
    staticTableBlock,
    parentUUID,
    parentStoredElements,
    updateParentComplexElementDetails,
    onCellDoubleClick,
    isInteractiveDesign
  } = props;
  const [picker, setPicker] = useState(false);
  const { updateComplexElementDetails, handleStoredElements, storedElements } = useContext(
    PageElementsContext,
  );
  const [, methods] = useDocumentContext(() => null);
  const { t } = useTranslation(NAMESPACES);

  const [, updateArtboard] = useArtboardContext(() => null);
  useEffect(() => {
    // close the sidebar when the element is removed from the block.
    return () => {
      updateArtboard({ [CONTEXT_KEYS.ELEMENT_SETTINGS_VISIBLE]: false });
    };
  }, []);

  const memoizedParentElements = useMemo(() => {
    if (staticTableBlock && parentStoredElements?.length) {
      const currentStore = methods.get();
      return parentStoredElements[0].contents.map(cont => currentStore[cont.uuid]);
    }
    return [];
  }, [staticTableBlock, parentStoredElements, methods]);

  const memoizedOtherCheckboxes = useMemo(() => {
    if (memoizedParentElements?.length) {
      return memoizedParentElements.filter(
        el => el && el?.fillableType === FILLABLE_CHECKBOX_TYPE && el?.uuid !== element.uuid,
      );
    }
    return storedElements.filter(
      el => el?.fillableType === FILLABLE_CHECKBOX_TYPE && el?.uuid !== element.uuid,
    );
  }, [storedElements, element, memoizedParentElements]);

  const updateStaticTableCheckbox = useCallback(
    details => {
      details.flowUUID = parentUUID;
      methods.updateBlock('static-table-cells-form-options', element.parentBlockUUID, details);
    },
    [element.parentBlockUUID, parentUUID],
  );

  const updateElementDetails = useCallback(
    (value, key) => {
      const details = {};
      if ([CHECK_TYPE_KEY, IS_VARIABLE_KEY, READ_ONLY_KEY, ...allStyleKeys].includes(key)) {
        const sameGroupElsUuids = (staticTableBlock ? memoizedParentElements : storedElements)
          .filter(
            el =>
              el?.formOptions?.groupName === element.formOptions.groupName &&
              el?.uuid !== element.uuid,
          )
          .map(el => el.uuid);
        details.extraFormElementsUuids = sameGroupElsUuids;
      }
      if (key === IS_VARIABLE_KEY) {
        details.style = {
          [IS_VARIABLE_KEY]: value,
        };
      } else if (styleKeys.includes(key)) {
        details.style = { [key]: value };
      } else if (key === borderColorKey)  {
        details.style = { border: { [key]: value } };
      } else {
        details.options = { [key]: value };
      }
      if (staticTableBlock) {
        details.uuid = element.uuid;
        updateStaticTableCheckbox(details);
      } else {
        updateComplexElementDetails(element.uuid, details, 'form-options');
      }
    },
    [updateComplexElementDetails, element, storedElements, staticTableBlock, memoizedParentElements, updateStaticTableCheckbox],
  );

  const debouncedUpdateComplexElementDetails = useRef(
    _.debounce(updateComplexElementDetails, TEXT_DEBOUNCE_TIME_OUT),
  ).current;

  const onFieldNameChange = useCallback(value => {
    // TODO : Check for duplicates
    debouncedUpdateComplexElementDetails(
      element.uuid,
      { options: { [FIELD_NAME_KEY]: value } },
      'form-options',
    );
  }, []);

  const onGroupNameChange = useCallback(value => {
    debouncedUpdateComplexElementDetails(
      element.uuid,
      { options: { [GROUP_NAME_KEY]: value } },
      'form-options',
    );
  }, []);

  const onConfirmGroupNameChange = value => {
    const isGroupElAlreadyAvailaibale = memoizedOtherCheckboxes.find(
      el => el.formOptions.groupName === value,
    );
    if (isGroupElAlreadyAvailaibale) {
      const { groupName, isReadOnly, checkType } = isGroupElAlreadyAvailaibale.formOptions;
      const { color, border, backgroundColor, isVariable } = isGroupElAlreadyAvailaibale.style;
      const details = {
        options: { groupName, isReadOnly, checkType },
        style: { color, border, backgroundColor, isVariable },
      };
      updateComplexElementDetails(element.uuid, details, 'form-options');
    }
  };

  const onOtherNameChange = useCallback(
    (value, el) => {
      if (staticTableBlock) {
        updateStaticTableCheckbox({
          uuid: element.uuid,
          options: { [FIELD_NAME_KEY]: value },
        });
      } else {
        debouncedUpdateComplexElementDetails(
          el.uuid,
          { options: { [FIELD_NAME_KEY]: value } },
          'form-options',
        );
      }
    },
    [
      staticTableBlock,
      element.uuid,
      debouncedUpdateComplexElementDetails,
      updateStaticTableCheckbox,
    ],
  );

  const showBackgroundColorPicker = useCallback(() => {
    setPicker(currentValue => {
      if (currentValue && currentValue.type === 'backgroundColor') {
        return false;
      }
      return { type: 'backgroundColor', color: element.style.backgroundColor };
    });
  }, [element]);

  const showBorderColorPicker = useCallback(() => {
    setPicker(currentValue => {
      if (currentValue && currentValue.type === 'borderColor') {
        return false;
      }
      return { type: 'borderColor', color: element.style.border?.borderColor };
    });
  }, [element]);

  const showElementColorPicker = useCallback(() => {
    setPicker(currentValue => {
      if (currentValue && currentValue.type === 'color') {
        return false;
      }
      return { type: 'color', color: element.style.border?.borderColor };
    });
  }, [element]);

  const onColorPick = useCallback(
    color => {
      if (picker.type === 'backgroundColor') {
        updateElementDetails(color.hex, 'backgroundColor');
        setPicker({ type: 'backgroundColor', color: color.hex });
      } else if (picker.type === 'borderColor') {
        updateElementDetails(color.hex, 'borderColor');
        setPicker({ type: 'borderColor', color: color.hex });
      } else if (picker.type === 'color') {
        updateElementDetails(color.hex, 'color');
        setPicker({ type: 'color', color: color.hex });
      }
    },
    [picker],
  );

  const addNewCheckbox = () => {
    const copyEl = deepCopy(element);
    copyEl.uuid = v4();
    copyEl.formOptions.fieldName = `check-box-${random4Characters()}`;
    if (staticTableBlock) {
      const currentElement = parentStoredElements[0].contents.find(el => el.uuid === element.uuid);
      updateParentComplexElementDetails(
        element.parentBlockUUID,
        {
          index: currentElement.row - 1,
          isTop: false,
          isBlock: true,
          newElement: copyEl,
          colIndex: currentElement.column,
          formUUID: copyEl.uuid
        },
        'static-table-add-row',
      );
      requestAnimationFrame(() =>
        onCellDoubleClick({ target: document.getElementById(copyEl.uuid), persist: dummyCallback }),
      );
    } else {
      copyEl.style.y = copyEl.style.y + 30;
      const additionalTextEl = checkBoxTextElement(t);
      additionalTextEl.uuid = v4();
      additionalTextEl.style.x = copyEl.style.x + additionalTextEl.style.x;
      additionalTextEl.style.y = copyEl.style.y + additionalTextEl.style.y;
      handleStoredElements([copyEl, additionalTextEl], true);
      refreshSelectedElementIds([copyEl.uuid]);
    }
  };

  const selectOtherCheckbox = euUuid => {
    if (staticTableBlock) {
      onCellDoubleClick({ target: document.getElementById(euUuid), persist: dummyCallback });
    } else {
      refreshSelectedElementIds([euUuid]);
    }
  };

  // check if parent width is greater than child width, then based on that set the height of the container to 100%
  // so the checkbox overlaps when container becomes smaller than the checkbox
  const parentElementStyle = document.getElementById(element.uuid);
  const parentWidth = parentElementStyle?.offsetWidth || parentElementStyle?.clientWidth;
  const childWidthGreaterThanParent = element?.style?.width > parentWidth;

  return (
    <>
      <CheckBoxSection
        borderColor={element.style?.border?.borderColor}
        backgroundColor={element.style?.backgroundColor}
        color={element.style?.color}
        style={{ pointerEvents: !editable && 'none' }}
        staticTableBlock={staticTableBlock}
        className={`complex-documents ${getCheckBoxClassName(
          element[OPTIONS_KEY][CHECK_TYPE_KEY],
        )}`}
        childWidthGreaterThanParent={childWidthGreaterThanParent}
      >
        <Checkbox
          checked={element.formOptions.isChecked}
          value={element.formOptions.isChecked}
        />
      </CheckBoxSection>
      {editable &&
        selected &&
        ReactDOM.createPortal(
          <FillableCheckBoxSettings
            element={element}
            updateElementDetails={updateElementDetails}
            onFieldNameChange={onFieldNameChange}
            onGroupNameChange={onGroupNameChange}
            settingsGetter={settingsGetter}
            FIELD_NAME_KEY={FIELD_NAME_KEY}
            READ_ONLY_KEY={READ_ONLY_KEY}
            getElementDetails={getElementDetails}
            addNewElement={addNewCheckbox}
            memoizedOtherFormElements={memoizedOtherCheckboxes}
            onOtherNameChange={onOtherNameChange}
            onConfirmGroupNameChange={onConfirmGroupNameChange}
            refreshSelectedElementIds={selectOtherCheckbox}
            isInteractiveDesign={isInteractiveDesign}
          />,
          document.getElementById('sidebar-absolute-container'),
        )}
      {editable &&
        selected &&
        ReactDOM.createPortal(
          <FillableElementWrapper>
            <Tooltip
              placement="bottom"
              title={t(
                ELEMENTS_SETTINGS_TOOLBAR.ELEMENT_BACKGROUND_TOOLTIP.key,
                ELEMENTS_SETTINGS_TOOLBAR.ELEMENT_BACKGROUND_TOOLTIP.value,
              )}
            >
              <ColorButton
                style={{ backgroundColor: element.style.backgroundColor, position: 'relative' }}
                onClick={showBackgroundColorPicker}
              />
            </Tooltip>
            <Tooltip
              placement="bottom"
              title={t(
                ELEMENTS_SETTINGS_TOOLBAR.ELEMENT_BORDER_COLOR_TOOLTIP.key,
                ELEMENTS_SETTINGS_TOOLBAR.ELEMENT_BORDER_COLOR_TOOLTIP.value,
              )}
            >
              <ColorButton
                style={{ backgroundColor: element.style.border?.borderColor, position: 'relative' }}
                onClick={showBorderColorPicker}
              />
            </Tooltip>
            <Tooltip
              placement="bottom"
              title={t(
                ELEMENTS_SETTINGS_TOOLBAR.ELEMENT_COLOR_TOOLTIP.key,
                ELEMENTS_SETTINGS_TOOLBAR.ELEMENT_COLOR_TOOLTIP.value,
              )}
            >
              <ColorButton
                style={{ backgroundColor: element.style.color, position: 'relative' }}
                onClick={showElementColorPicker}
              />
            </Tooltip>
            {picker && (
              <div style={{ position: 'absolute', top: '100%' }}>
                <ColorPicker
                  onChange={onColorPick}
                  onBlur={() => setPicker(false)}
                  color={picker.color}
                  qlEditor={true}
                />
              </div>
            )}
          </FillableElementWrapper>,
          document.getElementById('document-editor-toolbar'),
        )}
    </>
  );
};

export default ReusableCheckbox;
